import omit from 'lodash/omit';
import {useCallback} from 'react';
import {QueryKey, useQueryClient, UseQueryResult, useMutation, useQuery} from 'react-query';

import {
    AudienceCategories,
    AudienceCategoryInfo,
    AudienceInterestInfo,
    AudienceRequest,
    AudienceRequestUpdate,
    AudienceSegmentInfo,
    AudienceType,
    ScreeningQuestionTemplate,
    SurveyPreset,
    SurveySlug,
    isChoiceTemplateQuestion,
    isScreeningPageWithCondition
} from 'client/common/types';

import {
    getAudienceCategories,
    getAudienceInterests,
    getAudienceRequest,
    getAudienceSegments,
    updateAudienceRequest
} from 'client/common/utils/api-client';
import {queryClient} from 'client/common/utils/react-query';
import {AudienceTargeting} from 'client/common/utils/audience';
import {isYandexTeamDomain} from 'client/common/utils/yandex-team';

import {useLang} from 'client/common/contexts/lang';
import {useSurveySlug} from 'client/common/contexts/slug';

import {useBoxType} from 'client/common/hooks/box';
import {
    useCurrentRevisionId,
    useCountQuestions,
    useCurrentRevision
} from 'client/common/hooks/revision';
import {getRevisionContextKey} from 'client/common/hooks/revision/context';
import {useSurveyFeatures} from 'client/common/hooks/features';
import {useSurveyPreset} from 'client/common/hooks/box';
import {useUserRoles} from 'client/common/hooks/user';

import {getPaymentStatusKey, getPriceKey} from './payment';

const AUDIENCE_KEY = 'audience';
const REQUEST_KEY = 'request';
const CATEGORIES_KEY = 'categories';
const INTERESTS_KEY = 'interests';
const SEGMENTS_KEY = 'segments';

export function getAudienceRequestKey(slug: SurveySlug): QueryKey {
    return [AUDIENCE_KEY, REQUEST_KEY, slug];
}

export function getAudienceCategoriesKey(
    slug?: SurveySlug,
    categories?: AudienceCategories[]
): QueryKey {
    return slug
        ? [AUDIENCE_KEY, CATEGORIES_KEY, slug, categories || '']
        : [AUDIENCE_KEY, CATEGORIES_KEY];
}

export function getAudienceInterestsKey(): QueryKey {
    return [AUDIENCE_KEY, INTERESTS_KEY];
}

export function getAudienceSegmentsKey(): QueryKey {
    return [AUDIENCE_KEY, SEGMENTS_KEY];
}

async function fetchAudienceRequest(slug: SurveySlug): Promise<AudienceRequest> {
    const {data} = await getAudienceRequest({slug});

    if (data.type === AudienceType.Quotas) {
        const {categories = [], segments = [], interests = []} = data.quotas?.[0] ?? {};

        Object.assign(data, {categories, segments, interests});
    }

    return data;
}

async function fetchAudienceCategories(slug?: SurveySlug): Promise<AudienceCategoryInfo[]> {
    const {data} = await getAudienceCategories({slug});

    return data.categories;
}

async function fetchAudienceInterests(lang: Lang = 'ru'): Promise<AudienceInterestInfo[]> {
    const {data} = await getAudienceInterests({lang});

    return data.interests;
}

async function fetchAudienceSegments(): Promise<AudienceSegmentInfo[]> {
    const {data} = await getAudienceSegments();

    return data.segments;
}

export async function prefetchAudience(slug: SurveySlug, lang?: Lang) {
    await queryClient.prefetchQuery(getAudienceRequestKey(slug), async () =>
        fetchAudienceRequest(slug)
    );

    await queryClient.prefetchQuery(getAudienceCategoriesKey(slug), async () =>
        fetchAudienceCategories(slug)
    );

    await queryClient.prefetchQuery(getAudienceInterestsKey(), async () =>
        fetchAudienceInterests(lang)
    );

    await queryClient.prefetchQuery(getAudienceSegmentsKey(), fetchAudienceSegments);
}

export function useAudienceRequest(slug: SurveySlug): UseQueryResult<AudienceRequest> {
    return useQuery(getAudienceRequestKey(slug), async () => fetchAudienceRequest(slug));
}

export function useAudienceCategories(slug?: SurveySlug): UseQueryResult<AudienceCategoryInfo[]> {
    return useQuery(getAudienceCategoriesKey(slug), async () => fetchAudienceCategories(slug));
}

export function useAudienceInterests(): UseQueryResult<AudienceInterestInfo[]> {
    const lang = useLang();

    return useQuery(getAudienceInterestsKey(), async () => fetchAudienceInterests(lang));
}

export function useAudienceSegments(): UseQueryResult<AudienceSegmentInfo[]> {
    return useQuery(getAudienceSegmentsKey(), fetchAudienceSegments, {
        enabled: !isYandexTeamDomain // сегменты есть только во внешнем домене, нужны внешние куки
    });
}

export function useUpdateAudienceRequest(slug: SurveySlug) {
    const audienceKey = getAudienceRequestKey(slug);
    const priceKey = getPriceKey(slug);

    const revisionId = useCurrentRevisionId(slug);

    const paymentStatusKey = getPaymentStatusKey(slug);

    const contextKey = getRevisionContextKey(slug, revisionId);

    const queryClientFromContext = useQueryClient();

    const {mutateAsync} = useMutation(updateAudienceRequest, {
        onMutate: async params => {
            await queryClientFromContext.cancelQueries(audienceKey);

            const shapshot = queryClientFromContext.getQueryData(audienceKey);

            queryClientFromContext.setQueryData(audienceKey, (old: AudienceRequest) => ({
                ...old,
                ...omit(params, 'slug')
            }));

            return shapshot;
        },
        onError: (_err, _, shapshot) => {
            queryClientFromContext.setQueryData(audienceKey, shapshot);
        },
        onSettled: async () => {
            await queryClientFromContext.refetchQueries(priceKey);
            await queryClientFromContext.refetchQueries(contextKey);
            await queryClientFromContext.refetchQueries(paymentStatusKey);
            await queryClientFromContext.refetchQueries(audienceKey);
        }
    });

    return useCallback(
        async (update: AudienceRequestUpdate) => {
            const params = {slug, ...update};

            await mutateAsync(params);
        },
        [mutateAsync, slug]
    );
}

export function useCanRequestAudience(slug: SurveySlug): boolean {
    const request = useAudienceRequest(slug);
    const revision = useCurrentRevision(slug);
    const questionCount = useCountQuestions();

    if (request.status !== 'success' || !revision.data) {
        return false;
    }

    return !request.data.questionsLimit || questionCount <= request.data.questionsLimit;
}

export function useRequestAudienceTabs(audienceType?: AudienceType) {
    const slug = useSurveySlug();
    const features = useSurveyFeatures(slug);
    const userRoles = useUserRoles();

    const boxType = useBoxType(slug);

    const tabOptions = AudienceTargeting.getAvailableAudienceTabs({
        features: features?.data ?? {},
        boxType,
        audienceType,
        userRoles
    });

    return tabOptions;
}

export function useIsOwnAudience(slug: SurveySlug): boolean {
    const {data} = useAudienceRequest(slug);

    return data?.type === AudienceType.None;
}

export function useShouldDisplayBranding() {
    const slug = useSurveySlug();
    const {data} = useAudienceRequest(slug);
    const surverPreset = useSurveyPreset(slug);

    const audience = data?.type;

    return (
        audience === AudienceType.None ||
        audience === AudienceType.Manual ||
        audience === AudienceType.RespParamsOmi ||
        surverPreset === SurveyPreset.YandexActiveFeedback ||
        surverPreset === SurveyPreset.YandexPassiveFeedback
    );
}

export function useFixedScreeningOptionsSelected() {
    const slug = useSurveySlug();
    const revision = useCurrentRevision(slug);

    const templatesToOptionsSelected: Set<ScreeningQuestionTemplate> = new Set();

    if (!revision.data?.screening?.enabled) {
        return templatesToOptionsSelected;
    }

    Object.values(revision.data?.groups || []).forEach(group => {
        if (isScreeningPageWithCondition(group)) {
            group.children.forEach(question => {
                const q = revision.data?.questions[question.id];

                if (q && isChoiceTemplateQuestion(q)) {
                    templatesToOptionsSelected.add(q.template);
                }
            });
        }
    });

    return templatesToOptionsSelected;
}
